import { memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { History, UserRound, UserPlus } from 'lucide-react';
import { EntityListCard, type EntityListCardBadge } from '@/shared/components/entity-list-card';
import { ListCardActions } from '@/shared/components/list-card-actions';
import { LeadAvatar } from '@/shared/components/lead-avatar';
import { Badge } from '@/shared/ui/badge';
import { DropdownMenuItem } from '@/shared/ui/dropdown-menu';
import { Tooltip } from '@/shared/ui/tooltip';
import { cn } from '@/shared/lib/cn';
import { REFERRAL_STATUS, REWARD_STATUS, rejectReasonLabel } from '../model/referral-status';
import type { MgmReferralRow, MgmRewardRow } from '../model/types';
import { REFERRAL_ICON, REWARD_ICON } from './status-icons';

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
}

const CHANNEL: Record<MgmReferralRow['channel'], string> = {
  invite: 'Link de indicação',
  app: 'App',
  operator: 'Balcão (operador)',
};

/** Pessoa da indicação no formato que o `LeadAvatar` espera (mesmo rosto da página de Leads). */
function avatarLead(person: { userId: string | null; name: string; uniqueId?: string | null; gender?: string | null }, fallbackSeed: string) {
  return {
    _id: person.userId ?? fallbackSeed,
    profile: { name: person.name || '?', uniqueId: person.uniqueId ?? undefined },
    gender: person.gender ?? undefined,
  };
}

function rewardBadge(key: string, who: string, reward: MgmRewardRow | null): EntityListCardBadge | null {
  if (!reward) return null;
  const meta = REWARD_STATUS[reward.status];
  return {
    key,
    label: reward.amountText,
    icon: REWARD_ICON[reward.status],
    variant: meta.variant,
    size: 'sm',
    copyLines: [
      { label: `Recompensa ${who}`, value: `${reward.amountText} · ${meta.label}` },
      ...(reward.grantedAt ? [{ label: 'Concedida em', value: formatDate(reward.grantedAt) ?? '—' }] : []),
      ...(reward.lastError ? [{ label: 'Último erro', value: reward.lastError }] : []),
    ],
  };
}

interface Props {
  referral: MgmReferralRow;
  index: number;
  idPassbook: string;
  onOpen: (id: string) => void;
  /** Na aba do lead o indicador é o próprio lead. */
  hideReferrer?: boolean;
}

/**
 * Uma indicação na lista — o mesmo card da página de Leads (EntityListCard +
 * avatar DiceBear): o indicado em destaque, quem indicou no mini-avatar e nas
 * etiquetas, e as recompensas de cada lado como badges com detalhe copiável.
 */
export const MgmReferralCard = memo(function MgmReferralCard({ referral, index, idPassbook, onOpen, hideReferrer }: Props) {
  const navigate = useNavigate();
  const registered = Boolean(referral.referred.userId);
  const status = REFERRAL_STATUS[referral.status];

  const badges = useMemo<EntityListCardBadge[]>(
    () =>
      [
        {
          key: 'status',
          label: status.label,
          icon: REFERRAL_ICON[referral.status],
          variant: status.variant,
          copyLines: [
            { label: 'Status', value: referral.status === 'rejected' ? rejectReasonLabel(referral.rejectReason) : status.label },
            { label: 'Indicada em', value: formatDate(referral.indicatedAt) ?? '—' },
            ...(referral.registeredAt ? [{ label: 'Cadastrada em', value: formatDate(referral.registeredAt) ?? '—' }] : []),
            ...(referral.convertedAt ? [{ label: 'Convertida em', value: formatDate(referral.convertedAt) ?? '—' }] : []),
            { label: 'Origem', value: CHANNEL[referral.channel] ?? referral.channel },
          ],
        },
        rewardBadge('reward-referrer', 'do indicador', referral.rewards.referrer),
        rewardBadge('reward-referred', 'do indicado', referral.rewards.referred),
      ].filter((b): b is EntityListCardBadge => b !== null),
    [referral, status],
  );

  const thumbnail = (
    <div className="relative shrink-0">
      {registered ? (
        <LeadAvatar lead={avatarLead(referral.referred, referral.id)} className="size-11" />
      ) : (
        // Estático de propósito: numa lista cheia de "aguardando cadastro", ícone pulando distrai.
        <span className="grid size-11 place-items-center rounded-full border-2 border-dashed border-border bg-muted/40 text-muted-foreground">
          <UserPlus className="size-5" />
        </span>
      )}
      {hideReferrer ? null : (
        <Tooltip content={`Indicado por ${referral.referrer.name || 'cliente'}`} side="top">
          <span className="absolute -bottom-1 -right-1 inline-flex">
            <LeadAvatar
              lead={avatarLead(referral.referrer, referral.referrer.userId)}
              className="size-6 ring-2 ring-background"
              fallbackClassName="text-[9px]"
            />
          </span>
        </Tooltip>
      )}
    </div>
  );

  const subtitle = (
    <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5">
      {hideReferrer ? null : (
        <Badge
          variant="outline"
          className="h-5 max-w-[12rem] truncate whitespace-nowrap px-1.5 text-[10px] font-normal text-muted-foreground"
        >
          Indicado por {referral.referrer.name || 'cliente'}
        </Badge>
      )}
      <Badge
        variant="secondary"
        className="h-5 whitespace-nowrap border-transparent px-1.5 text-[10px] font-normal"
      >
        Indicada {formatDate(referral.indicatedAt)}
      </Badge>
      <Badge
        variant="secondary"
        className={cn('h-5 whitespace-nowrap border-transparent px-1.5 font-mono text-[10px] font-normal', registered && 'hidden sm:inline-flex')}
      >
        {referral.referred.keyMasked}
      </Badge>
    </div>
  );

  return (
    <EntityListCard
      selected={false}
      onToggle={() => undefined}
      selectable={false}
      onCardClick={() => onOpen(referral.id)}
      index={index}
      ariaLabel={`Ver indicação de ${referral.referred.name || referral.referred.keyMasked}`}
      title={referral.referred.name || 'Aguardando cadastro'}
      subtitle={subtitle}
      thumbnail={thumbnail}
      badges={badges}
      actions={
        <ListCardActions
          viewLabel="Ver histórico"
          viewIcon={History}
          onView={(e) => {
            e.stopPropagation();
            onOpen(referral.id);
          }}
          hideViewBelow="sm"
          menuChildren={
            <>
              <DropdownMenuItem onSelect={() => onOpen(referral.id)}>
                <History />
                Ver histórico
              </DropdownMenuItem>
              {hideReferrer ? null : (
                <DropdownMenuItem onSelect={() => navigate(`/loja/${idPassbook}/leads/${referral.referrer.userId}`)}>
                  <UserRound />
                  Abrir quem indicou
                </DropdownMenuItem>
              )}
              {referral.referred.userId ? (
                <DropdownMenuItem onSelect={() => navigate(`/loja/${idPassbook}/leads/${referral.referred.userId}`)}>
                  <UserRound />
                  Abrir indicado
                </DropdownMenuItem>
              ) : null}
            </>
          }
        />
      }
    />
  );
});
